<template>
  <div class="container-fluid py-3">
    <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap" style="gap: 8px">
      <h4 class="mb-0">
        <feather-icon
          icon="GridIcon"
          size="20"
          class="mr-50"
        />
        {{ $t('despacho.teamPanelTitle') }}
      </h4>
      <div class="d-flex align-items-center flex-wrap" style="gap: 10px">
        <b-form-checkbox
          v-model="autoRefresh"
          switch
        >
          {{ $t('despacho.teamPanel.autoRefresh') }}
        </b-form-checkbox>
        <!-- Filtro por disciplina: mesma estrutura do Embarque de equipes -->
        <select
          v-if="disciplineFilter.options.length || disciplineFilter.has_undisciplined"
          v-model="filterDiscipline"
          class="form-control form-control-sm tp-discipline-select"
          :aria-label="$t('despacho.teamPanel.disciplineFilter')"
        >
          <option :value="null">
            {{ $t('despacho.teamPanel.allDisciplines') }}
          </option>
          <option
            v-for="d in disciplineFilter.options"
            :key="d.id"
            :value="d.id"
          >
            {{ d.name }}
          </option>
          <option
            v-if="disciplineFilter.has_undisciplined"
            value="none"
          >
            {{ $t('despacho.teamPanel.noDiscipline') }}
          </option>
        </select>
        <button
          class="btn btn-sm btn-outline-secondary"
          @click="load"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="14"
            class="mr-25"
          />
          {{ $t('despacho.refresh') }}
        </button>
        <button
          class="btn btn-sm btn-outline-secondary"
          @click="$router.push({ name: 'despacho' })"
        >
          <feather-icon
            icon="ArrowLeftIcon"
            size="14"
            class="mr-25"
          />
          {{ $t('common.back') }}
        </button>
      </div>
    </div>

    <div
      v-if="!loading && !kpis.length"
      class="text-muted small mb-2"
    >
      {{ $t('despacho.teamPanel.noKpis') }}
    </div>

    <div
      v-if="loading"
      class="text-center py-5"
    >
      <b-spinner variant="primary" />
    </div>

    <div
      v-else-if="!teamsDaDisciplina.length"
      class="text-center text-muted py-5"
    >
      <feather-icon icon="UsersIcon" size="28" class="mb-1" />
      <p class="mb-0">
        {{ $t('despacho.teamPanel.emptyState') }}
      </p>
    </div>

    <!-- Agrupado por disciplina: o backend já ordena por disciplina, então
         cada grupo é contíguo. Cada grupo tem as próprias abas de posição
         (uma por posição com "Exibir no Painel" ligado, 100% dinâmico) e
         filtra só as equipes dele. -->
    <template v-else>
      <div
        v-for="group in teamGroups"
        :key="group.key"
        class="tp-group"
      >
        <div
          v-if="showGroupHeaders"
          class="tp-group-header"
        >
          <feather-icon
            icon="LayersIcon"
            size="14"
            class="mr-50"
          />
          <span class="tp-group-name">{{ group.name }}</span>
          <span class="tp-group-count">{{ $t('despacho.teamPanel.teamsCount', { count: group.teams.length }) }}</span>
        </div>

        <div
          v-if="group.tabs.length"
          class="tp-tabs mb-2"
          role="tablist"
        >
          <button
            type="button"
            role="tab"
            class="tp-tab"
            :class="{ 'tp-tab--active': !group.activeTab }"
            :aria-selected="String(!group.activeTab)"
            @click="selecionarAba(group.key, null)"
          >
            {{ $t('despacho.teamPanel.allPositions') }}
            <span class="tp-tab-count">{{ group.teams.length }}</span>
          </button>
          <button
            v-for="tab in group.tabs"
            :key="tab.key"
            type="button"
            role="tab"
            class="tp-tab"
            :class="{ 'tp-tab--active': group.activeTab === tab.key }"
            :style="group.activeTab === tab.key ? { borderBottomColor: positionColor(tab) } : null"
            :aria-selected="String(group.activeTab === tab.key)"
            :title="tab.label"
            @click="selecionarAba(group.key, tab.key)"
          >
            <span
              class="tp-tab-dot"
              :style="{ backgroundColor: positionColor(tab) }"
            />
            {{ tab.code ? tab.code + ' - ' : '' }}{{ tab.label }}
            <span class="tp-tab-count">{{ tab.count }}</span>
          </button>
        </div>

        <p
          v-if="!group.visibleTeams.length"
          class="text-muted small mb-2"
        >
          {{ $t('despacho.teamPanel.emptyState') }}
        </p>

        <div class="row">
          <div
            v-for="team in group.visibleTeams"
            :key="team.id"
            class="col-sm-6 col-lg-4 col-xl-3 mb-2 d-flex"
          >
            <!-- Resumo pra bater o olho: quem é, em que posição e há quanto
                 tempo, em qual ocorrência/onde e quem está embarcado. A faixa
                 lateral tem a mesma cor da aba da posição. -->
            <div
              class="tp-card"
              :style="{ borderLeftColor: positionColor(team.current_position) }"
              role="button"
              tabindex="0"
              @click="openTeamModal(team)"
              @keydown.enter="openTeamModal(team)"
            >
              <div class="tp-card-head">
                <div class="tp-card-title">
                  <div
                    class="tp-card-name"
                    :title="team.name"
                  >
                    {{ team.name }}
                  </div>
                  <div class="tp-card-sub">
                    <span
                      v-if="team.identifier_code && team.identifier_code !== team.name"
                      class="tp-card-code"
                    >{{ team.identifier_code }}</span>
                    <span v-if="team.type_name">{{ team.type_name }}</span>
                  </div>
                </div>
                <span
                  v-if="isFromOtherNode(team)"
                  class="tp-card-node"
                  :title="team.node_name"
                >
                  <feather-icon
                    icon="GitBranchIcon"
                    size="11"
                  />
                  {{ team.node_name }}
                </span>
              </div>

              <div
                class="tp-card-position"
                :style="{ backgroundColor: tint(positionColor(team.current_position), 0.12) }"
              >
                <span
                  v-if="team.current_position"
                  class="tp-card-position-name"
                  :title="team.current_position.name"
                >
                  <span
                    v-if="team.current_position.code"
                    class="tp-card-position-code"
                    :style="positionChipStyle(team.current_position)"
                  >{{ team.current_position.code }}</span>
                  {{ team.current_position.name }}
                </span>
                <span
                  v-else
                  class="tp-card-position-name tp-card-muted"
                >{{ $t('despacho.teamPanel.noPosition') }}</span>
                <span
                  v-if="team.current_position_at"
                  class="tp-card-timer"
                  :title="$t('despacho.teamPanel.timeInPositionLabel') + ' ' + formatDateTime(team.current_position_at)"
                >
                  <feather-icon
                    icon="ClockIcon"
                    size="12"
                  />
                  {{ elapsedHHMMSS(team.current_position_at) }}
                </span>
              </div>

              <div
                v-if="team.occurrence"
                class="tp-card-occ"
              >
                <div class="tp-card-occ-head">
                  <span class="tp-card-protocol">#{{ team.occurrence.protocol }}</span>
                  <span
                    v-if="team.occurrence.risk_color"
                    class="tp-card-risk"
                    :style="{ backgroundColor: team.occurrence.risk_color, color: textOn(team.occurrence.risk_color) }"
                  >{{ team.occurrence.risk_label }}</span>
                </div>
                <div
                  v-if="team.occurrence.reason"
                  class="tp-card-reason"
                  :title="team.occurrence.reason"
                >
                  {{ team.occurrence.reason }}
                </div>
                <div
                  class="tp-card-place"
                  :title="enderecoCompleto(team.occurrence)"
                >
                  <feather-icon
                    icon="MapPinIcon"
                    size="12"
                    class="tp-card-place-icon"
                  />
                  <span class="tp-card-place-text">
                    <span class="d-block text-truncate">{{ rua(team.occurrence) }}</span>
                    <span
                      v-if="regiao(team.occurrence)"
                      class="d-block text-truncate tp-card-place-region"
                    >{{ regiao(team.occurrence) }}</span>
                  </span>
                </div>
              </div>
              <div
                v-else
                class="tp-card-occ tp-card-occ--empty"
              >
                {{ $t('despacho.teamPanel.noOccurrence') }}
              </div>

              <div class="tp-card-foot">
                <template v-if="team.has_active_boarding && team.crew.length">
                  <div class="tp-avatars">
                    <span
                      v-for="(member, idx) in team.crew.slice(0, 4)"
                      :key="idx"
                      class="tp-avatar"
                      :title="member.name + (member.role ? ' — ' + member.role : '')"
                    >{{ iniciais(member.name) }}</span>
                    <span
                      v-if="team.crew.length > 4"
                      class="tp-avatar tp-avatar--more"
                    >+{{ team.crew.length - 4 }}</span>
                  </div>
                  <span class="tp-card-crew-count">
                    {{ $t('despacho.teamPanel.crewCount', { count: team.crew.length }) }}
                  </span>
                </template>
                <span
                  v-else-if="!team.has_active_boarding"
                  class="tp-card-alert"
                >
                  <feather-icon
                    icon="AlertTriangleIcon"
                    size="13"
                  />
                  {{ $t('despacho.teamPanel.noBoarding') }}
                </span>
                <span
                  v-else
                  class="tp-card-muted"
                >{{ $t('despacho.teamPanel.noCrew') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Modal: detalhe da equipe -->
    <b-modal
      v-model="showTeamModal"
      hide-footer
      centered
      size="lg"
      body-class="p-0"
      modal-class="team-modal"
    >
      <template v-if="selectedTeam">
        <div class="team-modal-header">
          <div class="team-modal-avatar">
            <feather-icon icon="UsersIcon" size="22" />
          </div>
          <div class="flex-grow-1" style="min-width: 0">
            <h5 class="mb-0 text-truncate">
              {{ selectedTeam.name }}
            </h5>
            <small class="text-muted">
              {{ [selectedTeam.identifier_code !== selectedTeam.name ? selectedTeam.identifier_code : null, selectedTeam.type_name].filter(Boolean).join(' · ') }}
            </small>
          </div>
          <span
            v-if="selectedTeam.current_position"
            class="badge"
            :style="positionChipStyle(selectedTeam.current_position)"
          >
            {{ selectedTeam.current_position.code ? selectedTeam.current_position.code + ' - ' : '' }}{{ selectedTeam.current_position.name }}
          </span>
        </div>

        <div class="p-3 pt-0">
          <div class="team-modal-info-row mb-3">
            <div class="team-modal-info-item">
              <feather-icon icon="ClockIcon" size="14" />
              <div>
                <span class="team-modal-info-label d-block">{{ $t('despacho.teamPanel.timeInPositionLabel') }}</span>
                <span class="font-weight-bold">
                  {{ selectedTeam.current_position_at ? elapsedHHMMSS(selectedTeam.current_position_at) : '—' }}
                </span>
              </div>
            </div>
            <div class="team-modal-info-divider" />
            <div class="team-modal-info-item">
              <feather-icon icon="CalendarIcon" size="14" />
              <div>
                <span class="team-modal-info-label d-block">{{ $t('despacho.teamPanel.lastPositionDateLabel') }}</span>
                <span class="font-weight-bold">
                  {{ selectedTeam.current_position_at ? formatDateTime(selectedTeam.current_position_at) : '—' }}
                </span>
              </div>
            </div>
          </div>

          <div
            v-if="selectedTeam.occurrence"
            class="team-card-occ mb-3"
          >
            <small class="d-block font-weight-bold">
              #{{ selectedTeam.occurrence.protocol }}
              <span v-if="selectedTeam.occurrence.reason"> · {{ selectedTeam.occurrence.reason }}</span>
            </small>
            <small class="d-block">
              {{ selectedTeam.occurrence.requester_name }} · {{ enderecoCompleto(selectedTeam.occurrence) }}
            </small>
          </div>

          <h6 class="team-modal-section-title mb-1">
            {{ $t('despacho.teamPanel.crewTitle') }}
          </h6>

          <div
            v-if="!selectedTeam.crew.length"
            class="text-muted small py-2"
          >
            {{ $t('despacho.teamPanel.noCrew') }}
          </div>

          <div
            v-else
            class="team-modal-crew-table"
          >
            <div class="team-modal-crew-head">
              <span>{{ $t('despacho.teamPanel.crewName') }}</span>
              <span>{{ $t('despacho.teamPanel.crewRole') }}</span>
              <span>{{ $t('despacho.teamPanel.crewBoardedAt') }}</span>
            </div>
            <div
              v-for="(member, idx) in selectedTeam.crew"
              :key="idx"
              class="team-modal-crew-row"
            >
              <span class="d-flex align-items-center" style="gap: 8px; min-width: 0">
                <span class="team-modal-crew-avatar">
                  {{ (member.name || '?').charAt(0).toUpperCase() }}
                </span>
                <span class="font-weight-bold text-truncate">{{ member.name }}</span>
              </span>
              <span class="text-muted">{{ member.role || '—' }}</span>
              <span class="text-muted">{{ member.boarded_at ? formatDateTime(member.boarded_at) : '—' }}</span>
            </div>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { BSpinner, BFormCheckbox } from 'bootstrap-vue'
import ElapsedTimeMixin from '@/mixins/ElapsedTimeMixin'
import RealtimeResyncMixin from '@/mixins/RealtimeResyncMixin'
import DispatchEventsMixin from '@/mixins/DispatchEventsMixin'

const DISCIPLINE_STORAGE_KEY = 'teampanel-discipline'

function storedDiscipline() {
  try {
    const value = localStorage.getItem(DISCIPLINE_STORAGE_KEY)
    if (!value) return null
    return value === 'none' ? 'none' : Number(value) || null
  } catch (e) {
    return null
  }
}

export default {
  name: 'DespachoTeamPanel',
  components: { BSpinner, BFormCheckbox },
  mixins: [ElapsedTimeMixin, RealtimeResyncMixin, DispatchEventsMixin],
  data() {
    return {
      loading: false,
      teams: [],
      kpis: [],
      disciplineFilter: { options: [], has_undisciplined: false },
      filterDiscipline: storedDiscipline(),
      // Aba de posição selecionada em cada grupo de disciplina (chave do grupo
      // → id da posição). Sem entrada = "Todas".
      abaPorGrupo: {},
      autoRefresh: true,
      refreshTimer: null,
      showTeamModal: false,
      selectedTeam: null,
    }
  },
  computed: {
    teamsDaDisciplina() {
      if (!this.filterDiscipline) return this.teams
      if (this.filterDiscipline === 'none') return this.teams.filter(t => !t.discipline_id)
      return this.teams.filter(t => Number(t.discipline_id) === Number(this.filterDiscipline))
    },

    // Cabeçalhos só fazem sentido quando há equipe com disciplina no nó —
    // num nó sem nenhuma, seria um único grupo "Sem disciplina".
    showGroupHeaders() {
      return this.disciplineFilter.options.length > 0
    },

    teamGroups() {
      const groups = []
      this.teamsDaDisciplina.forEach(team => {
        const key = team.discipline_id || 'none'
        let group = groups[groups.length - 1]
        if (!group || group.key !== key) {
          group = {
            key,
            name: team.discipline_name || this.$t('despacho.teamPanel.noDiscipline'),
            teams: [],
          }
          groups.push(group)
        }
        group.teams.push(team)
      })

      return groups.map(group => {
        const tabs = this.abasDoGrupo(group)
        // Aba lembrada que deixou de existir (ex.: última equipe "sem
        // disciplina" saiu daquela posição) volta pra "Todas".
        const selecionada = this.abaPorGrupo[group.key]
        const activeTab = tabs.some(t => t.key === selecionada) ? selecionada : null

        return {
          ...group,
          tabs,
          activeTab,
          visibleTeams: activeTab
            ? group.teams.filter(t => this.estaNaPosicao(t, activeTab))
            : group.teams,
        }
      })
    },
  },
  watch: {
    filterDiscipline(value) {
      try {
        if (value) localStorage.setItem(DISCIPLINE_STORAGE_KEY, String(value))
        else localStorage.removeItem(DISCIPLINE_STORAGE_KEY)
      } catch (e) {
        // sem storage o filtro só não é lembrado
      }
    },
  },
  mounted() {
    this.load()
    this.watchRealtimeResync(() => this.load())
    this.watchDispatchEvents()
    // Tempo real (watchDispatchEvents) já recarrega a cada posição/despacho
    // reportado — esse timer é só rede de segurança caso algum evento se
    // perca, não o mecanismo principal. Mesmo intervalo do Despacho/Index.
    this.refreshTimer = setInterval(() => {
      if (this.autoRefresh) this.load()
    }, 180000)
  },
  beforeDestroy() {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
  },
  methods: {
    isFromOtherNode(team) {
      const selectedNodeId = this.$store.state.selected_node?.id
      return !!team.node_id && !!selectedNodeId && team.node_id !== selectedNodeId
    },

    disciplineFilterIsValid() {
      if (this.filterDiscipline === 'none') return this.disciplineFilter.has_undisciplined
      return this.disciplineFilter.options.some(d => d.id === Number(this.filterDiscipline))
    },

    estaNaPosicao(team, positionKey) {
      return !!team.current_position && String(team.current_position.id) === positionKey
    },

    // Posição é cadastrada por disciplina: o grupo só mostra as posições da
    // própria disciplina. "Sem disciplina" não tem posições próprias, então
    // mostra as que as equipes dele estão ocupando.
    abasDoGrupo(group) {
      const posicoes = group.key === 'none'
        ? this.kpis.filter(k => group.teams.some(t => this.estaNaPosicao(t, k.key)))
        : this.kpis.filter(k => Number(k.discipline_id) === Number(group.key))

      return posicoes.map(k => ({
        ...k,
        count: group.teams.filter(t => this.estaNaPosicao(t, k.key)).length,
      }))
    },

    // Clicar na aba ativa de novo volta pra "Todas".
    selecionarAba(groupKey, positionKey) {
      const atual = this.abaPorGrupo[groupKey] || null
      this.$set(this.abaPorGrupo, groupKey, atual === positionKey ? null : positionKey)
    },

    onDispatchEvent() {
      if (this.autoRefresh) this.load()
    },

    async load() {
      this.loading = this.teams.length === 0
      try {
        const res = await this.$http.get(`${this.$store.state.api}team-panel`)
        this.kpis = res.data.kpis || []
        this.teams = res.data.data || []
        this.disciplineFilter = res.data.disciplines || { options: [], has_undisciplined: false }

        // Filtro lembrado de outra sessão pode apontar para disciplina que
        // não tem mais equipe aqui — volta para "todas" em vez de lista vazia.
        if (this.filterDiscipline && !this.disciplineFilterIsValid()) {
          this.filterDiscipline = null
        }

        // Modal aberto durante um refresh: troca pela versão atualizada da
        // mesma equipe, senão o modal fica mostrando dado congelado.
        if (this.selectedTeam) {
          this.selectedTeam = this.teams.find(t => t.id === this.selectedTeam.id) || this.selectedTeam
        }
      } catch (e) {
        // silencioso: próximo ciclo de auto-refresh tenta de novo
      } finally {
        this.loading = false
      }
    },

    openTeamModal(team) {
      this.selectedTeam = team
      this.showTeamModal = true
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '—'
      return new Date(dateStr).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    rua(occ) {
      const numero = occ.number && !String(occ.address || '').includes(occ.number) ? `, ${occ.number}` : ''
      return `${occ.address || ''}${numero}` || '—'
    },

    regiao(occ) {
      return [occ.neighborhood, occ.city].filter(Boolean).join(' · ')
    },

    enderecoCompleto(occ) {
      return [this.rua(occ), this.regiao(occ)].filter(Boolean).join(' — ')
    },

    iniciais(nome) {
      const partes = String(nome || '?').trim().split(/\s+/)
      const primeira = partes[0].charAt(0)
      const ultima = partes.length > 1 ? partes[partes.length - 1].charAt(0) : ''
      return (primeira + ultima).toUpperCase()
    },

    // Cor de fundo translúcida a partir do hex da posição (#rgb ou #rrggbb).
    tint(hex, alpha) {
      const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex || '')
      if (!m) return 'transparent'
      const h = m[1].length === 3 ? m[1].split('').map(c => c + c).join('') : m[1]
      const [r, g, b] = [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16))
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    },

    // Texto escuro em cor clara (ex.: verde #00f900), branco nas demais —
    // a cor vem do cadastro e pode ser qualquer uma.
    textOn(hex) {
      const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex || '')
      if (!m) return '#fff'
      const h = m[1].length === 3 ? m[1].split('').map(c => c + c).join('') : m[1]
      const [r, g, b] = [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16))
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? '#1f2937' : '#fff'
    },

    positionChipStyle(position) {
      const cor = this.positionColor(position)
      return { backgroundColor: cor, color: this.textOn(cor) }
    },

    // Cor vem do cadastro da posição (Nós > Disciplina > Posições) — a MESMA
    // função pra aba, tarja/badge do card e modal, pra nunca divergirem. Sem
    // cor configurada, cai num fallback pelas flags da posição (que a aba e
    // o card recebem iguais do backend) só pra não ficar tudo cinza.
    positionColor(position) {
      if (!position) return '#6B7280'
      if (position.color) return position.color
      if (position.marks_base_arrival) return '#16A34A'
      if (position.releases_occurrence) return '#D97706'
      return '#2563EB'
    },
  },
}
</script>

<style scoped>
.tp-group-header {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 4px 0 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #ebe9f1;
  color: #5e5873;
}
.dark-layout .tp-group-header {
  border-bottom-color: #3b4253;
  color: #d0d2d6;
}
.tp-group-name {
  font-weight: 600;
  margin-right: 6px;
}
.tp-group-count {
  font-size: 0.75rem;
  color: #6e6b7b;
}
.tp-discipline-select {
  width: 200px;
}
@media (max-width: 575.98px) {
  .tp-discipline-select {
    width: 100%;
  }
}
.tp-group {
  margin-bottom: 8px;
}
.tp-tabs {
  display: flex;
  flex-wrap: wrap;
  border-bottom: 1px solid #ebe9f1;
}
.dark-layout .tp-tabs {
  border-bottom-color: #3b4253;
}
.tp-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  margin-bottom: -1px;
  background: none;
  border: 0;
  border-bottom: 2px solid transparent;
  color: #6e6b7b;
  font-size: 0.85rem;
  white-space: nowrap;
  cursor: pointer;
}
.dark-layout .tp-tab {
  color: #b4b7bd;
}
.tp-tab:hover {
  color: #5e5873;
}
.dark-layout .tp-tab:hover {
  color: #d0d2d6;
}
.tp-tab--active {
  border-bottom-color: #7367f0;
  color: #5e5873;
  font-weight: 600;
}
.dark-layout .tp-tab--active {
  color: #d0d2d6;
}
.tp-tab-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.tp-tab-count {
  min-width: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: #f3f2f7;
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
}
.dark-layout .tp-tab-count {
  background: #283046;
}

.tp-card {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  background: #fff;
  border: 1px solid #e3e6ef;
  border-left: 5px solid #6b7280;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(34, 41, 47, 0.06);
  padding: 12px 12px 10px;
  color: #2b2a3a;
  cursor: pointer;
  transition: box-shadow 0.15s, transform 0.15s;
}
.tp-card:hover,
.tp-card:focus {
  outline: none;
  box-shadow: 0 6px 16px rgba(34, 41, 47, 0.12);
  transform: translateY(-1px);
}
.dark-layout .tp-card {
  background: #283046;
  border-color: #3b4253;
  color: #e4e6eb;
}
.tp-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}
.tp-card-title {
  min-width: 0;
}
.tp-card-name {
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tp-card-sub {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
  font-size: 0.78rem;
  color: #5e5873;
}
.dark-layout .tp-card-sub {
  color: #b4b7bd;
}
.tp-card-code {
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  font-weight: 600;
}
.tp-card-node {
  flex-shrink: 0;
  max-width: 45%;
  padding: 1px 6px;
  border-radius: 4px;
  background: #f3f2f7;
  font-size: 0.7rem;
  font-weight: 600;
  color: #5e5873;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dark-layout .tp-card-node {
  background: #161d31;
  color: #b4b7bd;
}
.tp-card-position {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  margin-bottom: 8px;
}
.tp-card-position-name {
  min-width: 0;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tp-card-position-code {
  display: inline-block;
  margin-right: 4px;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 700;
}
.tp-card-timer {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.9rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.tp-card-occ {
  font-size: 0.8rem;
  margin-bottom: 8px;
}
.tp-card-occ--empty {
  color: #82868b;
  font-style: italic;
}
.tp-card-occ-head {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
}
.tp-card-protocol {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.tp-card-risk {
  padding: 0 6px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 700;
}
.tp-card-reason {
  font-weight: 600;
  color: #5e5873;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dark-layout .tp-card-reason {
  color: #d0d2d6;
}
.tp-card-place {
  display: flex;
  gap: 4px;
  margin-top: 2px;
}
.tp-card-place-icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: #ea5455;
}
.tp-card-place-text {
  min-width: 0;
}
.tp-card-place-region {
  color: #6e6b7b;
}
.dark-layout .tp-card-place-region {
  color: #b4b7bd;
}
.tp-card-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid #ebe9f1;
  font-size: 0.78rem;
}
.dark-layout .tp-card-foot {
  border-top-color: #3b4253;
}
.tp-avatars {
  display: flex;
}
.tp-avatar {
  width: 26px;
  height: 26px;
  margin-left: -6px;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #e0e7ff;
  color: #3730a3;
  font-size: 0.65rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.tp-avatar:first-child {
  margin-left: 0;
}
.dark-layout .tp-avatar {
  border-color: #283046;
  background: #312e81;
  color: #e0e7ff;
}
.tp-avatar--more {
  background: #f3f2f7;
  color: #5e5873;
}
.tp-card-crew-count {
  font-weight: 600;
  color: #5e5873;
}
.dark-layout .tp-card-crew-count {
  color: #d0d2d6;
}
.tp-card-alert {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 700;
  color: #ea5455;
}
.tp-card-muted {
  color: #82868b;
}
.team-card-occ {
  background: #f8f7ff;
  border-radius: 6px;
  padding: 4px 8px;
  margin-top: 4px;
}
.dark-layout .team-card-occ {
  background: #1c2333;
}

.team-modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 1.1rem 1.25rem 1rem;
  border-bottom: 1px solid #ebe9f1;
}
.dark-layout .team-modal-header {
  border-bottom-color: #3b4253;
}
.team-modal-avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #eef1fd;
  color: #2f55d4;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.dark-layout .team-modal-avatar {
  background: #232a45;
}
.team-modal-info-row {
  display: flex;
  align-items: center;
  margin-top: 1rem;
}
.team-modal-info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: #6e6b7b;
  flex: 1;
  min-width: 0;
}
.dark-layout .team-modal-info-item {
  color: #b4b7bd;
}
.team-modal-info-divider {
  width: 1px;
  align-self: stretch;
  background: #ebe9f1;
  margin: 0 16px;
}
.dark-layout .team-modal-info-divider {
  background: #3b4253;
}
.team-modal-info-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.team-modal-section-title {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #5e5873;
}
.dark-layout .team-modal-section-title {
  color: #b4b7bd;
}
.team-modal-crew-table {
  border: 1px solid #ebe9f1;
  border-radius: 10px;
  overflow: hidden;
}
.dark-layout .team-modal-crew-table {
  border-color: #3b4253;
}
.team-modal-crew-head,
.team-modal-crew-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1.2fr;
  gap: 10px;
  padding: 8px 12px;
  align-items: center;
}
.team-modal-crew-head {
  background: #f8f8fa;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6e6b7b;
}
.dark-layout .team-modal-crew-head {
  background: #1c2333;
  color: #b4b7bd;
}
.team-modal-crew-row {
  font-size: 0.82rem;
  border-top: 1px solid #ebe9f1;
}
.dark-layout .team-modal-crew-row {
  border-top-color: #3b4253;
}
.team-modal-crew-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #eef1fd;
  color: #2f55d4;
  font-weight: 700;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.dark-layout .team-modal-crew-avatar {
  background: #232a45;
}
</style>
